/**
 * @file TabProfile.jsx
 * @description TabProfile component for displaying and managing tab profile.
 * @component TabProfile
 * @exports TabProfile - Main component for displaying and managing tab profile.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Button,
  Alert,
} from "@mui/material";
import {
  getcustomerprofilebyguid,
  confirmCustomer,
} from "../../../services/customerinfoModule/profileServices";
import DoneIcon from "@mui/icons-material/Done";
import SearchIcon from "@mui/icons-material/Search";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ProgressOverlay from "../../../common/components/ProgressOverlay";
import SearchCustomerModel from "../incoming/SearchCustomerModel";
import AddRelatedRecordModel from "../incoming/AddRelatedRecordModel";
import { useRegisterCsvExport } from "../../customerModule/customers/CsvExportContext.jsx";
import { PROFILE_COLUMNS } from "../../customerModule/customers/customerExportConfig.js";

// (xxx)-xxx-xxxx. Strips punctuation and a leading US country code first, so
// stored values like +15715551234 / 1-571-555-1234 / 5715551234 all normalise.
// Anything that isn't a 10-digit number after that is returned untouched rather
// than mangled — extensions and international numbers stay readable.
const formatPhone = (value) => {
  if (!value) return "—";
  const digits = String(value).replace(/\D/g, "");
  const local =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (local.length !== 10) return value;
  return `(${local.slice(0, 3)})-${local.slice(3, 6)}-${local.slice(6)}`;
};

const CustomerProfile = ({
  customerguid,
  agentType,
  callConnectionId,
  onRefreshOutgoingCalls,
}) => {
  const userguid = localStorage.getItem("userguid");
  const [localCustomerGuid, setLocalCustomerGuid] = useState(customerguid);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openSearchModel, setOpenSearchModel] = useState(false);
    useState(false);

  // Profile is a single record — wrap it so the export sees a one-row table.
  // MUST be memoised: a fresh array each render would re-fire the registration.
  const profileRows = useMemo(() => (profile ? [profile] : []), [profile]);
  useRegisterCsvExport({
    name: "profile",
    columns: PROFILE_COLUMNS,
    rows: profileRows,
    enabled: !loading,
  });

  // Update local customer GUID when prop changes
  useEffect(() => {
    setLocalCustomerGuid(customerguid);
  }, [customerguid]);

  useEffect(() => {
    if (localCustomerGuid) {
      fetchProfile();
    }
  }, [localCustomerGuid]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await getcustomerprofilebyguid({
        customerguid: localCustomerGuid,
      });
      setProfile(data?.[0] || null);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCustomer = async () => {
    setError(null);
    setSuccess(null);
    if (!callConnectionId) {
      setError("Call Connection ID is missing. Cannot confirm customer.");
      return;
    }
    setLoading(true);
    try {
      await confirmCustomer({
        customerguid: localCustomerGuid,
        callconnectionid: callConnectionId || null,
        userguid,
      });
      onRefreshOutgoingCalls();
      setSuccess("Customer confirmed successfully!");
    } catch (error) {
      console.error("Error confirming customer:", error);
      setError("Failed to confirm customer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchCustomer = () => {
    setOpenSearchModel(true);
  };

  const handleCustomerGuidFromSearchModel = (guid) => {
    if (guid) {
      setOpenSearchModel(false);
      setLocalCustomerGuid(guid);
    }
  };

  const handleCloseSearchModel = () => {
    setOpenSearchModel(false);
  };


  const showActionButtons = () => {
    return (agentType !== "Text" && agentType !== "Customer" &&
            agentType !== "Email")
  }

  const renderActionButtons = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          {showActionButtons() && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleConfirmCustomer}
                startIcon={<DoneIcon />}
              >
                Confirm Customer
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="primary"
                startIcon={<SearchIcon />}
                onClick={handleSearchCustomer}
              >
                Search Customer
              </Button>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {loading ? (
        <ProgressOverlay />
      ) : profile ? (
        <>
          {renderActionButtons()}
          <TableContainer component={Paper}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <strong>Customer ID</strong>
                  </TableCell>
                  <TableCell>{profile.customerid || "—"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Customer Phone</strong>
                  </TableCell>
                  <TableCell>{formatPhone(profile.customeruniqueid)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>First Name</strong>
                  </TableCell>
                  <TableCell>{profile.firstname}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Last Name</strong>
                  </TableCell>
                  <TableCell>{profile.lastname}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Email</strong>
                  </TableCell>
                  <TableCell>{profile.emailaddress}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    <strong>Customer GUID</strong>
                  </TableCell>
                  <TableCell>{profile.customerguid}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Typography>No profile found.</Typography>
      )}

      {openSearchModel && (
        <SearchCustomerModel
          open={openSearchModel}
          onClose={handleCloseSearchModel}
          onCustomerGuid={handleCustomerGuidFromSearchModel}
        ></SearchCustomerModel>
      )}

 
    </Box>
  );
};

export default CustomerProfile;